/**
 * Feature requests: the shape of the thing, shared by both ends.
 *
 * A request is one member's idea, a status the admin sets on it, and a thread
 * of messages between the two. Keeping the vocabulary here rather than in the
 * server means a status the client can render and a status the server can store
 * cannot drift apart — a renamed state is a compile error, not a request that
 * silently stops displaying.
 */
export const FEATURE_REQUEST_STATUSES = ['open', 'accepted', 'declined', 'completed'];
/**
 * A thread entry is either something somebody wrote or a note that the status
 * moved.
 *
 * Status changes live in the same thread as the messages deliberately: "why was
 * this declined" is answered by reading down the page in order, rather than by
 * correlating a badge against a separate log.
 */
export const FEATURE_REQUEST_MESSAGE_KINDS = ['message', 'status'];
export const MAX_FEATURE_REQUEST_TITLE_LENGTH = 90;
export const MAX_FEATURE_REQUEST_BODY_LENGTH = 2_000;
export const MAX_FEATURE_REQUEST_MESSAGE_LENGTH = 2_000;
/**
 * How many requests one member may have waiting for an answer.
 *
 * A ceiling rather than a rate limit: the thing being protected is the admin's
 * attention, not the database. Answered requests — accepted, declined or
 * completed — do not count, so somebody whose ideas keep getting looked at is
 * never blocked.
 */
export const MAX_OPEN_FEATURE_REQUESTS_PER_MEMBER = 10;
export function isFeatureRequestStatus(value) {
    return typeof value === 'string' && FEATURE_REQUEST_STATUSES.includes(value);
}
export function normalizeFeatureRequestTitle(value) {
    return typeof value === 'string' ? value.trim().slice(0, MAX_FEATURE_REQUEST_TITLE_LENGTH) : '';
}
export function normalizeFeatureRequestBody(value) {
    return typeof value === 'string' ? value.trim().slice(0, MAX_FEATURE_REQUEST_BODY_LENGTH) : '';
}
export function normalizeFeatureRequestMessage(value) {
    return typeof value === 'string' ? value.trim().slice(0, MAX_FEATURE_REQUEST_MESSAGE_LENGTH) : '';
}
/** The word on the badge. Short: it sits next to the title, not under it. */
export function featureRequestStatusLabel(status) {
    switch (status) {
        case 'open':
            return 'Waiting';
        case 'accepted':
            return 'Accepted';
        case 'declined':
            return 'Declined';
        case 'completed':
            return 'Done';
    }
}
/** What the status actually means for the person who asked. */
export function featureRequestStatusHint(status) {
    switch (status) {
        case 'open':
            return 'Nobody has looked at this yet.';
        case 'accepted':
            return 'This is going to happen, but it has not been built yet.';
        case 'declined':
            return 'This one is not going to happen.';
        case 'completed':
            return 'This has been built and is live.';
    }
}
/**
 * The sentence written into the thread when a status changes.
 *
 * Generated rather than typed so the record reads the same every time, and so
 * the admin only has to write the part that is actually about this request.
 */
export function featureRequestStatusNote(status) {
    switch (status) {
        case 'open':
            return 'Reopened this request.';
        case 'accepted':
            return 'Accepted this request.';
        case 'declined':
            return 'Declined this request.';
        case 'completed':
            return 'Marked this request as done.';
    }
}
//# sourceMappingURL=featureRequests.js.map